import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, User, Mail, Phone, Calendar, FileText, Save } from 'lucide-react';
import { Client, ClientUpdate } from '../types';

// Функция для форматирования телефона в простой формат +7XXXXXXXXXX
const formatPhone = (value: string) => {
  // Убираем все символы кроме цифр
  const digits = value.replace(/\D/g, '');
  
  // Если номер начинается с 8, заменяем на 7
  let normalizedDigits = digits.startsWith('8') ? '7' + digits.slice(1) : digits;
  
  // Если номер не начинается с 7, добавляем 7
  if (!normalizedDigits.startsWith('7') && normalizedDigits.length > 0) {
    normalizedDigits = '7' + normalizedDigits;
  }
  
  // Ограничиваем длину до 11 цифр
  normalizedDigits = normalizedDigits.slice(0, 11);
  
  // Возвращаем номер в простом формате +7XXXXXXXXXX
  if (normalizedDigits.length === 0) return '';
  return `+${normalizedDigits}`;
};

// Схема валидации для редактирования клиента
const editClientSchema = z.object({
  email: z.string().email('Введите корректный email').optional().or(z.literal('')),
  first_name: z.string().min(1, 'Имя обязательно'),
  last_name: z.string().min(1, 'Фамилия обязательна'),
  phone: z.string()
    .min(1, 'Телефон обязателен')
    .refine((value) => {
      const digits = value.replace(/\D/g, '');
      if (!digits.startsWith('7')) return false;
      return digits.length === 11;
    }, 'Номер телефона должен содержать ровно 10 цифр после +7'),
  date_of_birth: z.string().optional(),
  notes: z.string().optional(),
});

type EditClientFormData = z.infer<typeof editClientSchema>;

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  onSubmit: (id: number, data: ClientUpdate) => void;
  isLoading?: boolean;
}

const EditClientModal: React.FC<EditClientModalProps> = ({
  isOpen,
  onClose,
  client,
  onSubmit,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    watch,
  } = useForm<EditClientFormData>({
    resolver: zodResolver(editClientSchema),
    mode: 'onChange',
  });

  const phoneValue = watch('phone');

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhone(e.target.value);
    setValue('phone', formattedPhone, { shouldValidate: true });
  };

  useEffect(() => {
    if (client) {
      reset({
        email: client.email || '',
        first_name: client.first_name,
        last_name: client.last_name,
        phone: client.phone,
        date_of_birth: client.date_of_birth ? client.date_of_birth.slice(0, 10) : '',
        notes: client.notes || '',
      });
    }
  }, [client, reset]);

  const handleFormSubmit = (data: EditClientFormData) => {
    if (!client) return;
    const updateData: ClientUpdate = {
      ...data,
      email: data.email && data.email.trim() !== '' ? data.email : undefined,
      date_of_birth: data.date_of_birth ? new Date(data.date_of_birth).toISOString() : undefined,
    };
    onSubmit(client.id, updateData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Редактировать клиента</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>
        {/* Форма */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6">
          <div className="space-y-6">
            {/* Телефон */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Телефон *
              </label>
              <div className="relative">
                <input
                  {...register('phone', {
                    onChange: handlePhoneChange
                  })}
                  type="tel"
                  id="phone"
                  className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.phone ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="+79001234567"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone size={16} className="text-gray-400" />
                </div>
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Введите 10 цифр номера, +7 будет добавлено автоматически
              </p>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <input
                  {...register('email')}
                  type="email"
                  id="email"
                  className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="client@example.com (необязательно)"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail size={16} className="text-gray-400" />
                </div>
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Имя и Фамилия */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Имя *
                </label>
                <div className="relative">
                  <input
                    {...register('first_name')}
                    type="text"
                    id="first_name"
                    className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.first_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Иван"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={16} className="text-gray-400" />
                  </div>
                </div>
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Фамилия *
                </label>
                <div className="relative">
                  <input
                    {...register('last_name')}
                    type="text"
                    id="last_name"
                    className={`w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.last_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Иванов"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User size={16} className="text-gray-400" />
                  </div>
                </div>
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
                )}
              </div>
            </div>
            {/* Дата рождения */}
            <div>
              <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-2">
                Дата рождения
              </label>
              <div className="relative">
                <input
                  {...register('date_of_birth')}
                  type="date"
                  id="date_of_birth"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar size={16} className="text-gray-400" />
                </div>
              </div>
            </div>
            {/* Заметки */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Заметки
              </label>
              <div className="relative">
                <textarea
                  {...register('notes')}
                  id="notes"
                  rows={4}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Дополнительная информация о клиенте..."
                />
                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                  <FileText size={16} className="text-gray-400" />
                </div>
              </div>
            </div>
          </div>
          {/* Кнопки */}
          <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={!isValid || isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Сохранение...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Сохранить</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditClientModal; 